import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx'

export interface DropdownMenuTriggerArgs {
  readonly open: boolean
  readonly toggle: () => void
  readonly triggerRef: RefObject<HTMLButtonElement | null>
}

export interface DropdownMenuProps {
  /** Accessible name for the `role="menu"` panel. */
  readonly menuLabel: string
  readonly renderTrigger: (args: DropdownMenuTriggerArgs) => ReactNode
  /** Receives a `close()` that also returns focus to the trigger — call it
   * after an item's action completes so the menu doesn't stay open. */
  readonly renderMenu: (close: () => void) => ReactNode
  readonly className?: string
  /**
   * UID-568 — opt-in escape hatch for consumers rendered inside an
   * `overflow: hidden`/`auto` ancestor (the staging BOM tree's drawing/matched
   * cells, its horizontal-scroll wrapper, and the flat table's own
   * `overflow-x: auto` below the 900px breakpoint). Left `false` by default so
   * every OTHER consumer (`WorkspaceSelector`, "Add to project" bulk-bar menu)
   * stays byte-identical — neither opts in today, so portaling unconditionally
   * would be a needless behavior change for them. When `true`, the panel is
   * portaled to `document.body` — escaping both ancestor overflow clipping
   * AND the ancestor's stacking context — and positioned `fixed` from the
   * trigger's `getBoundingClientRect()`, flipped/clamped to stay inside the
   * viewport.
   */
  readonly escapeClipping?: boolean
}

/** `--space-2` (`tokens.css`) — the gap the in-flow panel gets via
 * `calc(100% + var(--space-2))`; the portaled panel needs the same gap
 * computed in pixels since it's positioned from viewport coordinates. */
const PANEL_GAP_PX = 8

/** `.dropdown-menu-panel`'s own `max-width`/`max-height` (`uploads.css`) —
 * the portaled panel isn't mounted yet when its position is first computed,
 * so viewport-flip/clamp math uses these upper bounds rather than a measured
 * rect. */
const PANEL_MAX_WIDTH_PX = 320
const PANEL_MAX_HEIGHT_PX = 320

/**
 * Shared keyboard-operable dropdown-menu shell (extracted from
 * `WorkspaceSelector.tsx`'s pattern, UID-140 S3): a trigger button
 * (`aria-haspopup="menu"`/`aria-expanded`), a `role="menu"` panel of
 * `role="menuitem"` children, click-outside dismiss, `Escape` returns focus
 * to the trigger, and Up/Down roving focus across the non-disabled menu
 * items. Every staging dropdown (associated-drawing, assign-to-part,
 * add-to-BOM) is this same shape with different content, so this is the one
 * place that shape's a11y behavior lives instead of five copies of it.
 */
export function DropdownMenu({
  menuLabel,
  renderTrigger,
  renderMenu,
  className,
  escapeClipping = false,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [portalPosition, setPortalPosition] = useState<{ top: number; left: number } | null>(null)

  // UID-568 — recompute the portaled panel's fixed-position coordinates from
  // the trigger's live viewport rect: on open, and kept in sync on scroll
  // (capture phase — scroll doesn't bubble, so a listener on `document` in the
  // bubble phase would miss scrolling inside `.staging-bom-tree-scroll`) and
  // resize while it stays open. Flips above the trigger and clamps
  // horizontally when the default placement would put the panel (partly)
  // outside the viewport — mirrors why `.add-to-project-menu`'s in-flow panel
  // (`parts.css`) already flips up for a bottom-pinned trigger; a `fixed`
  // panel gets no free ride from a scrollable ancestor if it goes off-screen.
  useLayoutEffect(() => {
    if (!open || !escapeClipping) {
      setPortalPosition(null)
      return
    }
    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const spaceBelow = window.innerHeight - rect.bottom
      const flipUp = spaceBelow < PANEL_MAX_HEIGHT_PX + PANEL_GAP_PX && rect.top > spaceBelow
      const top = flipUp
        ? Math.max(PANEL_GAP_PX, rect.top - PANEL_MAX_HEIGHT_PX - PANEL_GAP_PX)
        : rect.bottom + PANEL_GAP_PX
      const left = Math.min(
        Math.max(PANEL_GAP_PX, rect.left),
        window.innerWidth - PANEL_MAX_WIDTH_PX - PANEL_GAP_PX,
      )
      setPortalPosition((current) =>
        current?.top === top && current?.left === left ? current : { top, left },
      )
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, { capture: true, passive: true })
    window.addEventListener('resize', updatePosition, { passive: true })
    return () => {
      window.removeEventListener('scroll', updatePosition, { capture: true })
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, escapeClipping])

  // A portaled panel isn't mounted on the render that opens the menu (see
  // `panelMounted` below) — this must track when it's ACTUALLY in the DOM,
  // not just `open`, or `menuRef.current` is still null when this runs.
  const panelMounted = open && (!escapeClipping || portalPosition !== null)

  // QA Bug 3 (MEDIUM) — per the WAI-ARIA APG menu-button pattern, opening the
  // menu (from mouse or keyboard) should move focus onto the first
  // non-disabled menu item so Up/Down roving works immediately, instead of
  // leaving focus stranded on the trigger until the user Tabs or clicks in.
  useEffect(() => {
    if (!panelMounted) return
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    )
    const first = items.find((item) => !item.hasAttribute('disabled'))
    first?.focus()
  }, [panelMounted])

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      // UID-568 — once portaled (`escapeClipping`), the panel is no longer a
      // DOM descendant of `wrapperRef`, so it needs its own explicit "inside"
      // check or every click inside it would be treated as an outside click.
      if (wrapperRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return
      }
      setOpen(false)
    }
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  function moveFocus(direction: 1 | -1) {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    )
    const focusable = items.filter((item) => !item.hasAttribute('disabled'))
    if (focusable.length === 0) return
    const currentIndex = focusable.findIndex((item) => item === document.activeElement)
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + direction + focusable.length) % focusable.length
    focusable[nextIndex]?.focus()
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveFocus(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveFocus(-1)
    }
  }

  function close() {
    setOpen(false)
    triggerRef.current?.focus()
  }

  // `panelMounted`, not `open` — an `escapeClipping` menu renders NOTHING on
  // the frame `open` first flips true (no `portalPosition` yet): rendering an
  // in-flow panel there, even for one frame, would mount then immediately
  // unmount/remount it into the portal, which drops focus to `document.body`
  // (the exact regression this gating avoids — the position is computed in
  // `useLayoutEffect`, pre-paint, so this costs no visible delay).
  const panel = panelMounted && (
    <div
      role="menu"
      aria-label={menuLabel}
      className="dropdown-menu-panel"
      ref={menuRef}
      onKeyDown={handleMenuKeyDown}
      // UID-568 — only the portaled panel needs an inline fixed position;
      // the in-flow panel keeps its CSS `position: absolute` placement
      // untouched (`portalPosition` stays null whenever `escapeClipping` is
      // false, so this is `undefined` for every other consumer).
      style={
        portalPosition
          ? { position: 'fixed', top: portalPosition.top, left: portalPosition.left }
          : undefined
      }
    >
      {renderMenu(close)}
    </div>
  )

  return (
    <div className={cx('dropdown-menu-wrapper', className)} ref={wrapperRef}>
      {renderTrigger({ open, toggle: () => setOpen((current) => !current), triggerRef })}
      {escapeClipping ? panel && createPortal(panel, document.body) : panel}
    </div>
  )
}
